package com.milta.feature.aicredit.presentation

import kotlin.math.abs
import kotlin.math.roundToLong

/**
 * MIL-98 — number formatting of the AI credit screens (mobile + web). No `String.format` in
 * commonMain: decimals are rounded and laid out by hand. [separator] is the decimal mark of the
 * current language; thousands are grouped with a space, as on the mockups ("4 043 600").
 */
object AiCreditFormat {

    /** 1 $ = 1 000 000 weighted tokens (input × 1 + output × 5, Claude Haiku 4.5). */
    private const val WEIGHTED_TOKENS_PER_USD = 1_000_000.0

    /** Admin equivalent of an amount in euros: `€ × usdPerEur × 1 000 000` (A1, computed client-side). */
    fun weightedTokensForEur(eur: Double, usdPerEur: Double): Long = (eur * usdPerEur * WEIGHTED_TOKENS_PER_USD).roundToLong()

    /** "4 043 600" */
    fun grouped(value: Long): String {
        val digits = abs(value).toString()
        val groups = digits.reversed().chunked(3).joinToString(" ").reversed()
        return if (value < 0) "−$groups" else groups
    }

    /** "3,72" / "−0,03" — rounded half up to [decimals] digits, thousands grouped. */
    fun decimal(value: Double, decimals: Int, separator: String = ","): String {
        var factor = 1L
        repeat(decimals) { factor *= 10 }
        val scaled = (abs(value) * factor).roundToLong()
        val whole = grouped(scaled / factor)
        val fraction = (scaled % factor).toString().padStart(decimals, '0')
        val sign = if (value < 0 && scaled != 0L) "−" else ""
        return if (decimals == 0) "$sign$whole" else "$sign$whole$separator$fraction"
    }

    /** "3,72 €" — Admin only (R1: the user never sees euros). */
    fun eur(value: Double, decimals: Int = 2, separator: String = ","): String = "${decimal(value, decimals, separator)} €"

    /** Raw text of an amount field: "3,72" — no grouping, so it parses back. */
    fun eurInput(value: Double, separator: String = ","): String = decimal(value, 2, separator).replace(" ", "")

    /** Accepts a comma or a dot as decimal mark ("0,60", "0.6"); `null` when not a number. */
    fun parseDecimal(input: String): Double? {
        val normalized = input.trim().replace(" ", "").replace(' '.toString(), "").replace(',', '.')
        if (normalized.isEmpty() || normalized.count { it == '.' } > 1) return null
        if (!normalized.all { it.isDigit() || it == '.' || it == '-' }) return null
        return normalized.toDoubleOrNull()
    }

    /** Keeps what an amount field may contain: digits and one decimal mark, at most [maxDecimals] after it. */
    fun sanitizeAmountInput(input: String, maxDecimals: Int, maxWholeDigits: Int = 6): String {
        val out = StringBuilder()
        var hasMark = false
        var decimals = 0
        var whole = 0
        for (c in input) {
            when {
                c.isDigit() && hasMark -> if (decimals < maxDecimals) { out.append(c); decimals++ }
                c.isDigit() -> if (whole < maxWholeDigits) { out.append(c); whole++ }
                (c == ',' || c == '.') && !hasMark && maxDecimals > 0 -> { out.append(c); hasMark = true }
            }
        }
        return out.toString()
    }

    /**
     * User history share of the credit: "−1,2 %", "−<0,1 %" under a tenth (mockups E2, M3).
     * [percentTemplate] is "{percent} %" in the current language.
     */
    fun share(percent: Double, percentTemplate: String, separator: String): String {
        val value = if (percent > 0 && percent < MIN_VISIBLE_SHARE) "<${decimal(MIN_VISIBLE_SHARE, 1, separator)}" else decimal(percent, 1, separator)
        return "−" + percentTemplate.replace("{percent}", value)
    }

    private const val MIN_VISIBLE_SHARE = 0.1
}
